import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { Observable, of, pipe, throwError, timer } from 'rxjs';
import { delay, mergeMap, retry, retryWhen } from 'rxjs/operators';

export const getErrorMessage = (maxRetry: number) =>
  new Error(`Failed to load resource via XHR after ${maxRetry} attempts.`);

//Based on https://rocketknowledge.atlassian.net/wiki/spaces/TeamMembersPod/pages/164102488/R-SLC+Rocket+Reliability-Focused+System+Lifecycle#Retry-Pattern-with-Exponential-Backoff
const retryableErrorCodes: HttpStatusCode[] = [
  HttpStatusCode.InternalServerError,
  HttpStatusCode.BadGateway,
  HttpStatusCode.ServiceUnavailable,
  HttpStatusCode.GatewayTimeout,
];

const shouldRetry = (error: unknown): boolean => {
  if (error instanceof HttpErrorResponse) {
    return retryableErrorCodes.includes(error.status);
  }
  return false;
};

export const DEFAULT_MAX_RETRIES = 5;

export function delayedRetry(delayMs: number, maxRetry = DEFAULT_MAX_RETRIES) {
  let retries = maxRetry;

  return (src: Observable<any>) =>
    src.pipe(
      retryWhen((errors: Observable<any>) =>
        errors.pipe(
          delay(delayMs),
          mergeMap((error) => {
            retries--;
            return retries > 0 ? of(error) : throwError(() => getErrorMessage(maxRetry));
          })
        )
      )
    );
}

export function delayedRetryWithBackoff<T>(
  initialDelayMs: number,
  maxRetry = DEFAULT_MAX_RETRIES,
  backoffFactor = 1,
  preserveHttpErrorResponse = false
) {
  return retry<T>({
    count: maxRetry,
    delay: (error, retryCount) => {
      if (!shouldRetry(error)) {
        return throwError(() => error);
      }

      if (retryCount >= maxRetry) {
        return throwError(() => (preserveHttpErrorResponse ? error : getErrorMessage(maxRetry)));
      }

      const delayTime = initialDelayMs * Math.pow(backoffFactor, retryCount - 1);
      return timer(delayTime);
    },
  });
}
